import { useState, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

export default function ContactSection() {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [verificationAnswer, setVerificationAnswer] = useState('');
  
  // Simple math verification
  const num1 = 7;
  const num2 = 3;
  const correctAnswer = num1 + num2;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (parseInt(verificationAnswer) !== correctAnswer) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d4q5o31fcjnktac9qo10', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          message: ''
        });
        setVerificationAnswer('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <section id="contact" className="py-24 lg:py-32 relative">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(https://static.readdy.ai/image/c07ed6529feea951012003251bff6ba4/78a627228f114a68e878a20237e5938e.jpeg)' }}></div>
      <div className="absolute inset-0 bg-black/70"></div>
      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="text-sm font-semibold text-[#4d9c71] uppercase tracking-wider mb-4 whitespace-nowrap">
              {t('contact.sectionTitle')}
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              {t('contact.title')}
            </h2>
            <p className="text-lg text-gray-200">
              {t('contact.subtitleShort')}
            </p>
          </div>

          {/* Form */}
          <form id="contact-form" onSubmit={handleSubmit} data-readdy-form className="space-y-6 bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20">
            {/* Name & Email Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                  {t('contact.form.name')} {t('contact.form.required')}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-lg focus:ring-2 focus:ring-[#4d9c71] focus:border-[#4d9c71] outline-none transition-all text-sm text-gray-900"
                  placeholder={t('contact.form.namePlaceholder')}
                />
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                  {t('contact.form.email')} {t('contact.form.required')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-lg focus:ring-2 focus:ring-[#4d9c71] focus:border-[#4d9c71] outline-none transition-all text-sm text-gray-900"
                  placeholder={t('contact.form.emailPlaceholder')}
                />
              </div>
            </div>

            {/* Company & Phone Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Company */}
              <div>
                <label htmlFor="company" className="block text-sm font-medium text-white mb-2">
                  {t('contact.form.company')}
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-lg focus:ring-2 focus:ring-[#4d9c71] focus:border-[#4d9c71] outline-none transition-all text-sm text-gray-900"
                  placeholder={t('contact.form.companyPlaceholder')}
                />
              </div>

              {/* Phone */}
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white mb-2">
                  {t('contact.form.phone')} {t('contact.form.required')}
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-lg focus:ring-2 focus:ring-[#4d9c71] focus:border-[#4d9c71] outline-none transition-all text-sm text-gray-900"
                  placeholder={t('contact.form.phonePlaceholder')}
                />
              </div>
            </div>

            {/* Message */}
            <div>
              <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                {t('contact.form.message')} {t('contact.form.required')}
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                maxLength={500}
                rows={5}
                className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-lg focus:ring-2 focus:ring-[#4d9c71] focus:border-[#4d9c71] outline-none transition-all resize-none text-sm text-gray-900"
                placeholder={t('contact.form.messagePlaceholder')}
              />
              <div className="text-xs text-gray-200 mt-1">
                {formData.message.length}/500 {t('contact.form.charactersCount')}
              </div>
            </div>

            {/* Math Verification */}
            <div>
              <label htmlFor="verification" className="block text-sm font-medium text-white mb-2">
                {t('contact.form.verification')} {num1} + {num2}? {t('contact.form.required')}
              </label>
              <input
                type="number"
                id="verification"
                name="verification"
                value={verificationAnswer}
                onChange={(e) => setVerificationAnswer(e.target.value)}
                required
                className="w-full px-4 py-3 bg-white/90 border border-white/30 rounded-lg focus:ring-2 focus:ring-[#4d9c71] focus:border-[#4d9c71] outline-none transition-all text-sm text-gray-900"
                placeholder={t('contact.form.verificationPlaceholder')}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full px-8 py-4 bg-[#4d9c71] text-white font-semibold rounded-xl hover:shadow-xl hover:shadow-[#4d9c71]/50 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
            >
              {isSubmitting ? t('contact.form.sending') : t('contact.form.submit')}
            </button>

            {/* Status Messages */}
            {submitStatus === 'success' && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-800 text-sm">{t('contact.form.successMessage')}</p>
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 text-sm">{t('contact.form.errorMessage')}</p>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
